import { styled } from '@mui/material/styles'
import { useTheme, Container, Typography, ListItem } from '@mui/material'
import Layout from '../../../layouts'
import Page from '@/components/Page'
import { settingService, Section, GlobalSetting, SeoMetadata } from '@/services/page'
import pathService from '@/services/path'
import type { GetStaticPaths, GetStaticProps } from 'next'
import { APPS } from '@/appConfig'
import type { ParsedUrlQuery } from 'querystring'
import { notEmpty } from '@/utils/common'
import useApp from '@/hooks/useApp'
import { ReactNode } from 'react'

interface PathProps extends ParsedUrlQuery {
  appId: string
}

interface IndexProps {
  title?: string
  sections?: Section[]
  globalSetting?: GlobalSetting
  seoMetadataItemsCollection?: SeoMetadata[]
}

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  paddingTop: theme.spacing(8),
  [theme.breakpoints.up('md')]: {
    paddingTop: theme.spacing(11),
  },
}))

// ----------------------------------------------------------------------

TermsPage.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout variant="main">{page}</Layout>
}

export default function TermsPage({ title }: IndexProps) {
  const { id: appId, logo, host, title: rootTitle, description: rootDescription } = useApp()

  const CustomListItem = ({ children }: { children: ReactNode }) => (
    <ListItem sx={{ display: 'list-item' }}>{children}</ListItem>
  )

  return (
    <Page title="Terms">
      <RootStyle>
        <Container>
          <Typography variant="h1">Terms</Typography>
          <Typography variant="caption">Friday, December 23, 2022</Typography>
          <Typography variant="body1" mt={5}>
            PLEASE READ THESE TERMS OF USE (“AGREEMENT”) CAREFULLY BEFORE USING THE SERVICES OFFERED
            BY {rootTitle} (“COMPANY”). BY VISITING THE WEBSITE AND/OR MOBILE APP OR USING THE
            SERVICES IN ANY MANNER, YOU AGREE THAT YOU HAVE READ, UNDERSTOOD AND AGREE AND ACCEPT TO
            BE BOUND BY AND A PARTY TO THE TERMS AND CONDITIONS OF THIS AGREEMENT TO THE EXCLUSION
            OF ALL OTHER TERMS. IF THE TERMS OF THIS AGREEMENT ARE CONSIDERED AN OFFER, ACCEPTANCE
            IS EXPRESSLY LIMITED TO SUCH TERMS. IF YOU DO NOT UNCONDITIONALLY AGREE TO ALL THE TERMS
            AND CONDITIONS OF THIS AGREEMENT, YOU HAVE NO RIGHT TO USE THE APPS OR SERVICES. USE OF
            COMPANY’S SERVICES IS EXPRESSLY CONDITIONED UPON YOUR ASSENT TO ALL THE TERMS AND
            CONDITIONS OF THIS AGREEMENT, TO THE EXCLUSION OF ALL OTHER TERMS. IF YOU DO NOT AGREE
            TO THESE TERMS OF SERVICE, PLEASE DO NOT USE THIS WEBSITE OR THE APP.
          </Typography>
          <Typography variant="h5" mt={3}>
            Who can use
          </Typography>
          <Typography variant="body1" mt={1}>
            Company does not knowingly collect or solicit personal information from anyone under the
            age of 13 or knowingly allow such persons to register for the Services. If you are under
            13, please do not attempt to register for the Services or send any information about
            yourself to us, including your name, address, telephone number, or email address. No one
            under age 13 may provide any personal information to Company or on the Services. In the
            event that we learn that we have collected personal information from a child under age
            13 without verification of parental consent, we will delete that information as quickly
            as possible. If you believe that we might have any information from or about a child
            under 13, please contact us. You represent and warrant to Company that: (i) you are of
            legal age and you can form a binding contract with us and you are not prohibited by law
            from accessing or using the services; (ii) all registration information you submit is
            accurate and truthful; and (iii) you will maintain the accuracy of such information. You
            also certify that you are legally permitted to use and access the Services and take full
            responsibility for the selection and use of and access to the Services. This Agreement
            is void where prohibited by law, and the right to access the Services is revoked in such
            jurisdictions.
          </Typography>
          <Typography variant="h5" mt={3}>
            Placing Orders for Goods
          </Typography>
          <Typography variant="body1" mt={1}>
            By placing an Order for Goods through the Service, You warrant that You are legally
            capable of entering into binding contracts.
          </Typography>
          <Typography variant="h5" mt={3}>
            Your Information
          </Typography>
          <Typography variant="body1" mt={1}>
            If You wish to place an Order for Goods available on the Service, You may be asked to
            supply certain information relevant to Your Order including, without limitation, Your
            name, Your email, Your phone number, Your credit card number, the expiration date of
            Your credit card, Your billing address, and Your shipping information. You represent and
            warrant that: (i) You have the legal right to use any credit or debit card(s) or other
            payment method(s) in connection with any Order; and that (ii) the information You supply
            to us is true, correct and complete. By submitting such information, You grant us the
            right to provide the information to payment processing third parties for purposes of
            facilitating the completion of Your Order.
          </Typography>
        </Container>
      </RootStyle>
    </Page>
  )
}

export const getStaticPaths: GetStaticPaths<PathProps> = async () => {
  const { getDefaultPaths } = pathService()
  const paths = APPS.flatMap((app) => getDefaultPaths(app.id))

  return {
    paths,
    fallback: true,
  }
}

export const getStaticProps: GetStaticProps<IndexProps, PathProps> = async ({ params, locale }) => {
  if (!params) throw new Error('No path parameters found')

  const { appId } = params

  const { getGlobalSetting } = settingService(appId)
  const globalSetting = await getGlobalSetting({ locale })

  return {
    props: {
      title: globalSetting.seoMetadataItemsMap.title,
      globalSetting,
    },
    revalidate: 3600,
  }
}
